/**
 * LicenseLockout — Full-screen overlay when backend returns 402 (on-prem license invalid/expired).
 *
 * Listens for the 'vivya:license-invalid' CustomEvent dispatched by the API interceptor.
 * Shows a blocking overlay with license error details and contact info.
 * Supports light and dark themes via Tailwind dark: classes.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LicenseLockout = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const handler = (e) => {
      setErrorMsg(e.detail?.error || t('licenseLockout.errorDefault'));
      setVisible(true);
    };
    window.addEventListener('vivya:license-invalid', handler);
    return () => window.removeEventListener('vivya:license-invalid', handler);
  }, [t]);

  if (!visible) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-sm"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-12 max-w-md w-[90%] text-center shadow-2xl">
        {/* Lock icon */}
        <div className="text-6xl mb-4" aria-hidden="true"></div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {t('licenseLockout.title')}
        </h2>

        <p className="text-base font-semibold text-red-600 dark:text-red-400 mb-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
          {errorMsg}
        </p>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
          {t('licenseLockout.description')}
        </p>

        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-700 dark:text-gray-300">
          <strong>{t('licenseLockout.support')}</strong> support@vivyawatch.com
          <br />
          <strong>{t('licenseLockout.portal')}</strong>{' '}
          <a
            href="https://vivyawatch.com/support"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-600 dark:text-purple-400 hover:underline"
          >
            vivyawatch.com/support
          </a>
        </div>

        <button
          onClick={() => window.location.reload()}
          className="mt-6 px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        >
          {t('licenseLockout.retry')}
        </button>
      </div>
    </div>
  );
};

export default LicenseLockout;

